'use client';

import { cn } from 'cn';
import { useAtom } from 'jotai';

import { FEATURE_FLAGS, usePosthogFeatureFlag } from '@/shared-measurement';
import {
  MessageSender,
  OptionRenderType,
  type GetProjectQuery,
} from '@/survey-graphql';

import { getProjectChatProps } from './project-response-chat-props.utils';
import { PROJECT_CHAT_CLASSNAMES } from './project-response-chat.types';
import { useResponderEmail } from './project-response-insight-info.hooks';
import { enumStringEquals } from './project-response.helpers';
import { imageChoiceSelectionAtom } from './project-usability-image-choice.atoms';
import { useMobileAppPreloadDriver } from './project-usability-mobile-app-preload.hooks';
import type { UsabilityResponseSession } from './project-usability-response-session.hooks';
import { useReportProblemSubmit } from './survey-project-response-report-problem.hooks';

interface UsabilityResponseStageOptions extends Pick<
  UsabilityResponseSession,
  | 'isSaving'
  | 'isSubmitting'
  | 'isConversationOver'
  | 'effectiveIsConversationOver'
  | 'messages'
  | 'insightLoading'
  | 'handleConversationOver'
  | 'activityStore'
  | 'insightId'
> {
  project: GetProjectQuery['project'];
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  conversationOverText: string | null;
}

export type UsabilityResponseStage = ReturnType<
  typeof useUsabilityResponseStage
>;

export function useUsabilityResponseStage({
  project,
  orgSettings,
  conversationOverText,
  isSaving,
  isSubmitting,
  isConversationOver,
  effectiveIsConversationOver,
  messages,
  insightLoading,
  handleConversationOver,
  activityStore,
  insightId,
}: UsabilityResponseStageOptions) {
  const responderEmail = useResponderEmail();

  const chatProps = getProjectChatProps({
    project,
    orgSettings,
    responseState: { isSaving, isSubmitting, isConversationOver },
  });

  const lastAssistantMessage = messages.findLast((m) =>
    enumStringEquals(m.sender, MessageSender.Assistant)
  );
  const isImageChoiceActive = Boolean(
    lastAssistantMessage &&
    lastAssistantMessage.type?.toLowerCase() === 'multiplechoice' &&
    lastAssistantMessage.optionRenderType === OptionRenderType.Media &&
    !effectiveIsConversationOver
  );

  const currentQuestionId = lastAssistantMessage?.questionId ?? null;
  const currentQuestionIndex = currentQuestionId
    ? project.questions.findIndex((q) => q.id === currentQuestionId)
    : -1;

  const { isPreloading: isMobileAppPreloading } = useMobileAppPreloadDriver({
    questions: project.questions,
    currentQuestionIndex,
  });

  const [imageChoiceSelection, setImageChoiceSelection] = useAtom(
    imageChoiceSelectionAtom
  );
  const activeImageChoiceMessageId =
    isImageChoiceActive && lastAssistantMessage
      ? lastAssistantMessage.id
      : null;
  if (activeImageChoiceMessageId !== imageChoiceSelection.messageId) {
    setImageChoiceSelection({
      messageId: activeImageChoiceMessageId,
      value: '',
    });
  }

  const { flag: reportProblemFlag } = usePosthogFeatureFlag(
    FEATURE_FLAGS.REPORT_PROBLEM_FEATURE
  );
  const handleReportProblemSubmit = useReportProblemSubmit({
    project,
    insightId,
    responderEmail,
  });

  const sharedChatProps = {
    ...chatProps,
    insightLoading,
    messages,
    isConversationOver: effectiveIsConversationOver,
    onConversationOver: handleConversationOver,
    conversationOverText,
    activityStore,
    onReportProblemSubmit:
      reportProblemFlag(handleReportProblemSubmit) ?? undefined,
    classes: {
      root: cn(
        PROJECT_CHAT_CLASSNAMES.usability,
        'group-data-[is-sidebar-open=false]:hidden'
      ),
    },
  };

  return {
    lastAssistantMessage,
    isImageChoiceActive,
    isMobileAppPreloading,
    imageChoiceSelection,
    setImageChoiceSelection,
    reportProblemFlag,
    handleReportProblemSubmit,
    sharedChatProps,
  };
}
